<template>
  <v-dialog :value="model.active" width="450" persistent>
    <v-card v-if="selected">
      <div class="d-flex justify-space-between align-center pr-2">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left>mdi-upload</v-icon>
          Upload Model
        </v-card-title>
        <v-btn icon @click="closeDialog"><v-icon>fa-close</v-icon></v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-text class="pt-5">
        <v-form v-model="model.valid">
          <!-- Model Name Field -->
          <v-text-field
            outlined
            dense
            clearable
            v-virtual-keyboard
            label="Model Name"
            placeholder="Enter Name..."
            v-model="selected.name"
            :error-messages="nameErrorMessage"
            :disabled="isModelPreSelected"
            @input="validateModelName">
          </v-text-field>
          
          <!-- Version Field -->
          <v-text-field
            outlined
            dense
            readonly
            label="Version"
            v-model="selected.version"
            hint="Version is automatically assigned"
            persistent-hint>
          </v-text-field>
          
          <v-checkbox v-model="selected.isSvf2" label="Is SVF2" hide-details="auto" class="mt-2"></v-checkbox>
          <v-checkbox v-model="selected.uploadInChunks" label="Upload in chunks" hide-details="auto" class="mt-2"></v-checkbox>
          <!-- SVG removed for security - can contain executable JavaScript -->
          <v-file-input
            accept=".dwg,.dwf,.dxf,.idw,.ipt,.iam,.rvt,.rfa,.skp,.ifc,.stl,.obj,.fbx,.3ds,.iges,.step,.sldprt,.sldasm,.nwd,.nwc,.dwfx,.sat,.catpart,.catproduct,.par,.asm,.prt,.neu,.rhino,.3dm,.dgn,.pdf,.bmp,.gif,.jpeg,.jpg,.png,.tiff,.tif"
            dense
            clearable
            label="Selected File"
            class="mt-4"
            hide-details="auto"
            v-model="selectedFile"></v-file-input>

          <v-select
            dense
            outlined
            clearable
            label="Storage Region Override"
            class="mt-4"
            v-model="selected.apsRegion"
            :items="apsRegions"
            item-text="name"
            item-value="id"
            hint="Optional: Override company default region for this model"
            persistent-hint>
          </v-select>
          <v-progress-linear
            v-if="isUploading"
            :value="(progress_models / maxProgress_modls) * 100"
            max
            color="primary"
            class="mt-2"
            height="15">
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="red" elevation="0" text @click="closeDialog"> close </v-btn>
        <v-spacer></v-spacer>
        <v-btn elevation="0" :disabled="!model.hasChanges" @click="discard"> discard </v-btn>
        <v-btn
          color="primary"
          elevation="0"
          @click="uploadForgeModel"
          :disabled="!isFormValid">
          upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { ForgeModelVersion } from '@/scripts/js/forgeModels';
  import { $handleError } from '@/utils';
  import { $isEqual } from '@Utils';
  import { mapGetters } from 'vuex';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'UploadModel',
    data() {
      return {
        progress_models: 0,
        selectedFile: null,
        isUploading: false,
        maxProgress_modls: 100,
        selected: {
          name: '',
          version: '',
          isSvf2: false,
          uploadInChunks: false,
          apsRegion: null,
        },
        selectedCemented: {
          name: '',
          version: '',
          isSvf2: false,
          uploadInChunks: false,
          apsRegion: null,
        },
        nameErrorMessage: '',
        isModelPreSelected: false, // When true, the model is pre-selected and cannot be changed
        model: {
          active: false,
          loading: false,
          valid: true,
          hasChanges: false,
        },
      };
    },

    computed: {
      ...mapGetters('ADMIN_MODELS', {
        masters: 'GET_ENTRIES',
      }),

      isFormValid() {
        const hasFile = !!this.selectedFile;
        const hasValidName = !!this.selected.name && !this.nameErrorMessage;
        const hasValidVersion = !!this.selected.version;
        return hasFile && hasValidName && hasValidVersion && this.model.valid && this.model.hasChanges;
      },

      apsRegions() {
        return [
          { id: null, name: 'Use Company Default' },
          { id: 1, name: 'US' },
          { id: 2, name: 'EMEA' }
        ];
      }
    },

    methods: {
      open() {
        this.model.active = true;
        this.selected = new ForgeModelVersion();
        this.selected.version = '1'; // Default version for new models
        this.nameErrorMessage = '';
        this.isModelPreSelected = false;
        this.cementSelected();
      },
      
      openForNewVersion(model) {
        this.model.active = true;
        this.selected = new ForgeModelVersion();
        this.selected.name = model.name;
        this.nameErrorMessage = '';
        this.isModelPreSelected = true;
        this.setVersionNumber();
        this.cementSelected();
      },
      
      setVersionNumber() {
        if (!this.selected?.name) {
          this.selected.version = '1';
          return;
        }

        // Find the highest version for this model name
        const existingVersions = this.masters
          .filter(m => m.name === this.selected.name)
          .map(m => parseInt(m.version) || 0);
        
        if (existingVersions.length === 0) {
          // New model - start with version 1
          this.selected.version = '1';
        } else {
          // Existing model - increment highest version
          const highestVersion = Math.max(...existingVersions);
          this.selected.version = `${highestVersion + 1}`;
        }
      },
      
      validateModelName() {
        if (!this.selected.name) {
          this.nameErrorMessage = '';
          this.selected.version = '1';
          return;
        }
        
        // Clear any previous error messages
        this.nameErrorMessage = '';
        
        // Automatically set the version number based on existing models
        this.setVersionNumber();
        this.checkForChanges();
      },
      
      saveChanges() {
        this.$emit('updateConfirmed', {
          selected: this.selected,
          index: this.index,
        });
        this.cementSelected();
        this.closeDialog();
      },
      reset() {
        this.model.active = false;
        this.selected = {
          name: '',
          version: '',
          isSvf2: false,
          uploadInChunks: false,
          apsRegion: null,
        };
        this.selectedCemented = {
          name: '',
          version: '',
          isSvf2: false,
          uploadInChunks: false,
          apsRegion: null,
        };
        this.selectedFile = null;
        this.nameErrorMessage = '';
        this.isModelPreSelected = false;
        this.model.hasChanges = false;
      },
      closeDialog() {
        if (this.model.hasChanges) {
          const answer = window.confirm('You have unsaved changes on this page! leave anyway?');
          if (answer) {
            this.reset();
            this.cementSelected();
            this.discard();
          }
        } else {
          this.reset();
        }
      },
      discard() {
        this.selected = structuredClone(this.selectedCemented);
        this.model.hasChanges = false;
      },
      cementSelected() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.model.active) return;

        this.model.hasChanges = !$isEqual(this.selected, this.selectedCemented) || !!this.selectedFile;
      },

      onFileChanged(event) {
        if (event.target.files.length == 0) {
          this.selectedFile = null;
          return;
        }
        this.selectedFile = event.target.files[0];
      },
      broswe() {
        document.getElementById('browseModelFile').click();
      },

      uploadForgeModel() {
        if (!this.selectedFile) {
          this.$dialog.notify.warning('Please select file first.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (!this.selected.name) {
          this.$dialog.notify.warning('Please Enter the model name.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (!this.selected.version) {
          this.$dialog.notify.warning('Please Enter the model version.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        
        // Additional validation
        if (this.nameErrorMessage) {
          this.$dialog.notify.warning(this.nameErrorMessage, {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        
        // Check if this exact version already exists
        if (this.$store.state.uploadedModels.some((o) => o.name == this.selected.name && o.version == this.selected.version)) {
          this.$dialog.notify.warning('This model version already exists.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        
        this.progress_models = 0;
        this.maxProgress_modls = 100;
        this.isUploading = true;

        var onProgress = (progress, maxProgress) => {
          this.progress_models = progress;
          this.maxProgress_modls = maxProgress;
        };
        this.$store.state.forgeService.uploadVersionToAsset(
          !!this.selected.uploadInChunks,
          this.$route.params.id,
          this.selected,
          this.selectedFile,
          onProgress,
          (version) => {
            this.isUploading = false;
            this.$emit('uploadSuccess', version);
            this.$emit('dispose3DViewer');
            this.selected = new ForgeModelVersion();
            this.$dialog.notify.success('Uploaded successfully, translating in progress...', {
              position: 'top-right',
              timeout: 3000,
            });
            this.selectedFile = null;
            this.cementSelected();
            this.closeDialog();
          },
          (error) => {
            log(error);
            this.isUploading = false;
            $handleError({
              message: 'Uploading failed, please try again later.',
            });
          },
        );
      },
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      selectedFile() {
        this.checkForChanges();
      },
      'selected.name'() {
        if (!this.isModelPreSelected) {
          this.setVersionNumber();
        }
      }
    },
  };
</script>
